import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { UserGear, FloppyDisk, ArrowsLeftRight, WarningCircle } from "@phosphor-icons/react";

export default function BillingContact() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");
  const [vat, setVat] = useState("");
  const [reverseCharge, setReverseCharge] = useState(false);
  const [vatStatus, setVatStatus] = useState("");
  const [isEu, setIsEu] = useState(false);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = () => {
    api.get("/billing/contact")
      .then(({ data }) => {
        setData(data); setName(data.billing_name || ""); setEmail(data.billing_email || "");
        setLine1(data.address_line1 || ""); setCity(data.address_city || "");
        setPostcode(data.address_postcode || ""); setCountry(data.address_country || "");
        setVat(data.vat_number || ""); setReverseCharge(!!data.reverse_charge);
        setVatStatus(data.vat_status || ""); setIsEu(!!data.is_eu);
      })
      .catch((e) => { if (e?.response?.status === 403) setHidden(true); });
  };
  useEffect(load, []);

  if (hidden || !data) return null;

  const dirty = name !== (data.billing_name || "") || email !== (data.billing_email || "")
    || line1 !== (data.address_line1 || "") || city !== (data.address_city || "")
    || postcode !== (data.address_postcode || "") || country !== (data.address_country || "")
    || vat !== (data.vat_number || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.put("/billing/contact", {
        billing_name: name, billing_email: email,
        address_line1: line1, address_city: city, address_postcode: postcode, address_country: country,
        vat_number: vat,
      });
      setData((d) => ({ ...d, billing_name: r.billing_name, billing_email: r.billing_email,
        address_line1: r.address_line1, address_city: r.address_city,
        address_postcode: r.address_postcode, address_country: r.address_country,
        vat_number: r.vat_number, reverse_charge: r.reverse_charge }));
      setVat(r.vat_number || ""); setReverseCharge(!!r.reverse_charge);
      setVatStatus(r.vat_status || ""); setIsEu(!!r.is_eu);
      if (r.vat_status === "invalid") toast.error("VAT number couldn't be verified with VIES — standard VAT will apply");
      else if (r.vat_status === "unverified" && r.vat_number) toast.message("We couldn't reach the VIES service — save again shortly to verify your VAT number");
      else toast.success("Billing details updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save billing details");
    } finally { setSaving(false); }
  };

  const transfer = async () => {
    if (!target) return;
    setTransferring(true);
    try {
      await api.post("/billing/transfer-ownership", { user_id: target });
      toast.success("Billing ownership transferred");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not transfer ownership");
      setTransferring(false);
    }
  };

  const inCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy placeholder:text-navy/30 focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none transition-colors";
  const candidates = data.transfer_candidates || [];
  const targetEmail = candidates.find((c) => c.id === target)?.email;

  return (
    <motion.div data-testid="billing-contact-panel"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }}
      className="card-surface overflow-hidden mt-6">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-navy/[0.06]">
          <UserGear size={18} weight="fill" className="text-navy/70" />
        </span>
        <div>
          <h2 className="font-heading font-bold text-lg text-navy tracking-tight">Billing contact</h2>
          <p className="text-xs text-navy/70">The subscriber shown to your team and used for billing &amp; receipts.</p>
        </div>
      </div>

      <div className="p-6 grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Edit contact */}
        <div>
          <div className="overline text-navy/65 mb-3">Subscriber details</div>
          <div className="space-y-4">
            <label className="block">
              <span className="overline text-navy/70 block mb-1.5">Contact name</span>
              <input className={inCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" data-testid="billing-name-input" />
            </label>
            <label className="block">
              <span className="overline text-navy/70 block mb-1.5">Billing email</span>
              <input type="email" className={inCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="billing@practice.co" data-testid="billing-email-input" />
            </label>
            {(!data.address_line1 || !data.address_postcode) && (
              <div className="flex items-start gap-2 rounded-xl bg-[#F37A24]/10 border border-[#F37A24]/25 px-3.5 py-2.5" data-testid="billing-address-nudge">
                <WarningCircle size={16} weight="fill" className="text-[#B4560F] mt-0.5 shrink-0" />
                <p className="text-xs text-[#8a4410] leading-relaxed">
                  Add your billing address so your PDF receipts are complete and HMRC-ready.
                </p>
              </div>
            )}
            <label className="block">
              <span className="overline text-navy/70 block mb-1.5">Postal address</span>
              <input className={inCls} value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="Address line 1" data-testid="billing-address-line1" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input className={inCls} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City / Town" data-testid="billing-address-city" />
              <input className={inCls} value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode" data-testid="billing-address-postcode" />
            </div>
            <input className={inCls} value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (e.g. United Kingdom)" data-testid="billing-address-country" />
            <label className="block">
              <span className="overline text-navy/70 block mb-1.5">VAT number <span className="text-navy/40 normal-case">(optional)</span></span>
              <input className={inCls} value={vat} onChange={(e) => setVat(e.target.value)} placeholder="e.g. IE1234567X" data-testid="billing-vat-number" />
            </label>
            {reverseCharge ? (
              <div className="flex items-start gap-2 rounded-xl bg-positive/[0.08] border border-positive/25 px-3.5 py-2.5" data-testid="billing-reverse-charge-note">
                <WarningCircle size={16} weight="fill" className="text-positive mt-0.5 shrink-0" />
                <p className="text-xs text-navy/75 leading-relaxed">
                  VAT number verified with VIES — EU reverse charge applies. You'll be charged <b>net (no VAT)</b> and must account for VAT yourself at your local rate. Your VAT number prints on every receipt.
                </p>
              </div>
            ) : isEu && vat && vatStatus === "invalid" ? (
              <div className="flex items-start gap-2 rounded-xl bg-[#E5484D]/[0.07] border border-[#E5484D]/30 px-3.5 py-2.5" data-testid="billing-vat-invalid-note">
                <WarningCircle size={16} weight="fill" className="text-[#E5484D] mt-0.5 shrink-0" />
                <p className="text-xs text-navy/75 leading-relaxed">
                  We couldn't verify this VAT number with the EU VIES service, so <b>standard VAT will apply</b>. Please double-check the number (including the country prefix).
                </p>
              </div>
            ) : isEu && vat && vatStatus === "unverified" ? (
              <div className="flex items-start gap-2 rounded-xl bg-[#F37A24]/10 border border-[#F37A24]/25 px-3.5 py-2.5" data-testid="billing-vat-unverified-note">
                <WarningCircle size={16} weight="fill" className="text-[#B4560F] mt-0.5 shrink-0" />
                <p className="text-xs text-[#8a4410] leading-relaxed">
                  The VIES verification service didn't respond just now. Standard VAT applies for the moment — save again shortly to verify and switch to reverse charge.
                </p>
              </div>
            ) : null}
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="billing-contact-save"
            className="mt-4 inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100"
            style={{ background: dirty ? "#0E0F3F" : "#94a3b8" }}>
            <FloppyDisk size={16} weight="fill" /> {saving ? "SAVING…" : "Save details"}
          </button>
          <p className="text-xs text-navy/65 mt-3 leading-relaxed" data-testid="billing-stripe-note">
            Used for receipts, digests and payment alerts — your postal address prints on your PDF receipts. Your <span className="font-medium text-navy">card and invoices</span> are held securely in Stripe — open <span className="font-medium text-navy">Manage billing</span> above to change them. Updates made there sync back here automatically.
          </p>
        </div>

        {/* Transfer ownership */}
        <div className="lg:border-l lg:border-slate-100 lg:pl-6">
          <div className="overline text-navy/65 mb-3">Transfer billing ownership</div>
          {candidates.length === 0 ? (
            <p className="text-sm text-navy/70" data-testid="transfer-no-candidates">
              You're the only admin. Add another admin under <span className="font-medium text-navy">Practice → Team &amp; Users</span> to be able to hand over billing.
            </p>
          ) : !confirming ? (
            <>
              <p className="text-sm text-navy/70 mb-3">Hand subscription management to another admin. They'll then manage the plan and billing contact.</p>
              <select className={inCls} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="transfer-target-select">
                <option value="">Choose an admin…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name ? `${c.name} — ${c.email}` : c.email}</option>
                ))}
              </select>
              <button onClick={() => setConfirming(true)} disabled={!target} data-testid="transfer-ownership-btn"
                className="mt-4 inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl border border-navy text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-navy">
                <ArrowsLeftRight size={16} weight="bold" /> Transfer ownership
              </button>
            </>
          ) : (
            <div className="rounded-xl border border-warn/40 bg-warn/[0.06] p-4" data-testid="transfer-confirm">
              <p className="text-sm text-navy">
                Transfer billing ownership to <b>{targetEmail}</b>? Their details will become the billing contact, and you'll lose the ability to change the plan (read-only view).
              </p>
              <div className="flex gap-2 mt-3">
                <button onClick={transfer} disabled={transferring} data-testid="transfer-confirm-btn"
                  className="inline-flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: "#F37A24" }}>
                  {transferring ? "TRANSFERRING…" : "Yes, transfer"}
                </button>
                <button onClick={() => setConfirming(false)} disabled={transferring} data-testid="transfer-cancel-btn"
                  className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
